import React, { useReducer } from 'react';

import { css } from '@emotion/css';
import { Dialog } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useStyles } from '@/hooks/useStyles.ts';
import { HandledErrorPage } from '@/pages/HandledErrorPage.tsx';
import { Queries } from '@/queries/Queries.ts';
import { QueryAwait } from '@/queries/QueryAwait.tsx';
import { SideSheet } from '@/uiKit/atoms/SideSheet/SideSheet.tsx';
import { CardHeader } from '@/uiKit/atoms/card/CardHeader.tsx';
import { CenteredLoader } from '@/uiKit/atoms/pending/CenteredLoader.tsx';
import { CarePlanRenewalForm } from '@/uiKit/organisms/PatientCreationAndRenewal/CarePlan/CarePlanRenewalForm.tsx';
import { CarePlanRenewalFormActions } from '@/uiKit/organisms/PatientCreationAndRenewal/CarePlan/CarePlanRenewalFormActions.tsx';
import { CarePlanRenewalFormContent } from '@/uiKit/organisms/PatientCreationAndRenewal/CarePlan/CarePlanRenewalFormContent.tsx';
import {
  useGeneratePrescription,
  useRenewPatient,
  useSignPrescriptionAndRenewPatient,
} from '@/uiKit/organisms/PatientCreationAndRenewal/PatientCreationAndRenewalActions.ts';
import { patientRenewalWizard } from '@/uiKit/organisms/PatientCreationAndRenewal/PatientRenewalWizard.ts';
import { PrescriptionInfoForm } from '@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionInfo/PrescriptionInfoForm.tsx';
import { PrescriptionInfoFormActions } from '@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionInfo/PrescriptionInfoFormActions.tsx';
import { PrescriptionInfoFormContent } from '@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionInfo/PrescriptionInfoFormContent.tsx';
import { PrescriptionInfoFormTitle } from '@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionInfo/PrescriptionInfoFormTitle.tsx';
import { PrescriptionSignActions } from '@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionSign/PrescriptionSignActions.tsx';
import { PrescriptionSignContent } from '@/uiKit/organisms/PatientCreationAndRenewal/PrescriptionSign/PrescriptionSignContent.tsx';

export type PatientRenewalProps = {
  patientId: string;
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
};

export const PatientRenewal: React.FC<PatientRenewalProps> = props => {
  return (
    <SideSheet open={props.open} onClose={props.onClose}>
      <QueryAwait
        errorFallback={HandledErrorPage}
        pendingFallback={CenteredLoader}
      >
        <CarePlanRenewalHeader patientId={props.patientId} />
        <CarePlanRenewalContent {...props} />
      </QueryAwait>
    </SideSheet>
  );
};

const CarePlanRenewalHeader: React.FC<{ patientId: string }> = ({
  patientId,
}) => {
  const { t } = useTranslation();

  const carePlans = Queries.practitioner.useCarePlans(patientId);

  return (
    <CardHeader
      title={
        carePlans.renewal_suggestion === 'add_insulin'
          ? t('diabetesForms.addInsulin')
          : t('diabetesForms.renew')
      }
      variant="contained"
    />
  );
};

export type CarePlanRenewalContentProps = {
  patientId: string;
  onClose: () => void;
  onSubmit: () => void;
};

const CarePlanRenewalContent: React.FC<CarePlanRenewalContentProps> = ({
  patientId,
  onClose,
  onSubmit,
}) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(
    patientRenewalWizard.reducer,
    patientRenewalWizard.initialState,
  );
  const generatePrescription = useGeneratePrescription();
  const patient = Queries.practitioner.usePatient(patientId);
  const condition = Queries.condition.useCondition(patientId);
  const renewPatient = useRenewPatient(onSubmit);
  const signAndRenewPatient = useSignPrescriptionAndRenewPatient(onSubmit);
  const carePlanDirectives = Queries.practitioner.useCarePlanDirectives({
    patientId: patient.id,
  });

  return (
    <>
      <CarePlanRenewalForm
        carePlanInfo={
          state.CarePlan ?? {
            diabetesType: condition.type,
            insulinScheme:
              condition.medicationSchema !== 'noInsulin'
                ? condition.medicationSchema
                : 'insulinPen',
            termDate: condition.termDate,
            inclusionCriteria:
              carePlanDirectives.previousCarePlanData?.telemonitoringCriteria,
            description: carePlanDirectives.previousCarePlanData?.description,
            descriptionAttachment:
              carePlanDirectives.previousCarePlanData?.descriptionAttachment,
          }
        }
        onSubmit={data => dispatch({ action: 'TO_PrescriptionInfo', data })}
        className={styles.form}
      >
        <CarePlanRenewalFormContent patient={patient} />
        <CarePlanRenewalFormActions
          onBack={data => {
            dispatch({ action: 'SET_DATA_CarePlan', data });
            onClose();
          }}
        />
      </CarePlanRenewalForm>
      <Dialog
        open={
          state.step === 'PrescriptionInfo' || state.step === 'PrescriptionSign'
        }
        onClose={() => {
          if (state.step === 'PrescriptionSign') {
            dispatch({ action: 'BACK_TO_PrescriptionInfo' });
          }
          dispatch({ action: 'BACK_TO_CarePlan' });
        }}
        maxWidth={state.step === 'PrescriptionInfo' ? 'xs' : 'md'}
        fullWidth
        classes={{ paper: styles.dialog }}
      >
        {state.step === 'PrescriptionInfo' && (
          <PrescriptionInfoForm
            prescriptionInfo={state.PrescriptionInfo ?? patient}
            onGeneratePrescription={data =>
              generatePrescription(
                {
                  patientInfo: patient,
                  carePlan: state.CarePlan,
                  prescriptionInfo: data,
                },
                ({ prescription }) => {
                  dispatch({
                    action: 'TO_PrescriptionSign',
                    data: { ...data, prescription },
                  });
                },
              )
            }
            onUploadPrescription={file => {
              renewPatient(patient, condition, state.CarePlan, {}, file);
            }}
          >
            <PrescriptionInfoFormTitle
              onExit={data => dispatch({ action: 'BACK_TO_CarePlan', data })}
            />
            {state.step === 'PrescriptionInfo' && (
              <PrescriptionInfoFormContent patientInfo={patient} />
            )}
            <PrescriptionInfoFormActions
              onBack={data => dispatch({ action: 'BACK_TO_CarePlan', data })}
              submitTitle={t('diabetesForms.renewPatient')}
            />
          </PrescriptionInfoForm>
        )}
        {state.step === 'PrescriptionSign' && (
          <>
            <CardHeader
              title={t('diabetesForms.prescriptionSignature')}
              variant="contained"
              onExit={() => {
                dispatch({
                  action: 'BACK_TO_PrescriptionInfo',
                });
                dispatch({ action: 'BACK_TO_CarePlan' });
              }}
            />
            <PrescriptionSignContent
              prescription={state.PrescriptionInfo.prescription}
            />
            <PrescriptionSignActions
              submitTitle={t('diabetesForms.signAndRenewPatient')}
              onBack={() => {
                dispatch({
                  action: 'BACK_TO_PrescriptionInfo',
                });
              }}
              onSubmit={() => {
                signAndRenewPatient(
                  patient,
                  condition,
                  state.CarePlan,
                  state.PrescriptionInfo,
                );
              }}
            />
          </>
        )}
      </Dialog>
    </>
  );
};

const makeStyles = () => ({
  form: css({
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    overflow: 'hidden',
  }),
  dialog: css({
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    alignItems: 'center',
  }),
});
